import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import backend from "../../backend";
import PiezaPlanoMaterial from "../../components/PiezaPlanoMaterial";
import useIsMobile from "../../hooks/useIsMobile";
import WaitAsync from "../../components/WaitAsync";
import { formatInt, formatPrice, maskRut, maskPhone } from "../../utils";
import BinimelizPDFLayout from "../../components/design-system/BinimelizPDFLayout";

// Persistencia del draft del form en localStorage. Permite que el state
// sobreviva la navegación a /pieces/new y vuelta, evitando que click en
// "Nueva pieza (calculadora)" borre lo que la usuaria ya cargó.
// v2 (26-ago): el precio del ítem pasó de TOTAL de línea a UNITARIO — el bump
// del key descarta drafts viejos con la semántica anterior (habrían quedado
// con montos malos al restaurar).
// v3 (27-ago): los ítems ganan tipoTrabajo (ref/fab/rep) + descLibre +
// componenteReparado + planos — drafts v2 no tienen esos campos.

// Contactos elegibles de un cliente: los de la ficha + el legacy contactPerson.
const contactosDe = (c) => {
  if (!c) return [];
  const arr = (c.contactos || []).map(x => ({ nombre: x.nombre, email: x.email || "", area: x.area || "" }));
  if (c.contactPerson && !arr.some(x => x.nombre === c.contactPerson)) {
    arr.push({ nombre: c.contactPerson, email: c.email || "", area: "" });
  }
  return arr.filter(x => x.nombre);
};

const DRAFT_KEY = "binimeliz:quotationForm:drafts:v4"; // LISTA de borradores (multi, 02-sep)
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h — y en localStorage: sobrevive cerrar el navegador (01-sep, Maxi perdió un borrador al apagar el PC)

// Multi-borrador: cada cotizacion a medias es UNA entrada con su id. El vendedor
// puede tener varias (lo interrumpen con una urgente y ninguna pisa a la otra).
const loadDrafts = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return [];
    const lista = JSON.parse(raw);
    if (!Array.isArray(lista)) return [];
    const vivas = lista.filter(d => d?.savedAt && Date.now() - d.savedAt <= DRAFT_MAX_AGE_MS);
    if (vivas.length !== lista.length) localStorage.setItem(DRAFT_KEY, JSON.stringify(vivas));
    return vivas;
  } catch { return []; }
};
const upsertDraft = (draft) => {
  try {
    const lista = loadDrafts().filter(d => d.draftId !== draft.draftId);
    lista.unshift(draft);
    localStorage.setItem(DRAFT_KEY, JSON.stringify(lista.slice(0, 10)));
  } catch {}
};
const removeDraft = (draftId) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(loadDrafts().filter(d => d.draftId !== draftId)));
  } catch {}
};
// Borrador de Nueva Pieza (calculadora) — para que el cotizador lo muestre y
// nadie sienta que "la pieza quedo en otra parte".
const PIEZA_DRAFT_KEY = "binimeliz:pieceForm:draft:v1";
const loadPiezaAMedias = () => {
  try {
    const d = JSON.parse(localStorage.getItem(PIEZA_DRAFT_KEY) || "null");
    if (!d?.savedAt || Date.now() - d.savedAt > DRAFT_MAX_AGE_MS) return null;
    if (!(d.name || d.sku || (Array.isArray(d.processes) && d.processes.length))) return null;
    return d;
  } catch { return null; }
};

const etiquetaDraft = (d) => {
  const cli = d.client?.name || d.client?.label || "sin cliente";
  const desc = (d.name || "").trim();
  const min = Math.round((Date.now() - d.savedAt) / 60000);
  const hace = min < 60 ? `hace ${min} min` : `hace ${Math.round(min / 60)} h`;
  return `${cli}${desc ? " — " + desc.slice(0, 40) : ""} · ${hace}`;
};


// ─── estilos del select ───────────────────────────────────────────────────────
const selectStyles = {
  option:      s => ({ ...s, color: "black" }),
  control:     s => ({ ...s, flexDirection: "row", background: "#161616", borderColor: "#333", minHeight: 34 }),
  input:       s => ({ ...s, flexDirection: "row", color: "#ddd" }),
  singleValue: s => ({ ...s, color: "#ddd" }),
  placeholder: s => ({ ...s, color: "#999" }),
  menu:        s => ({ ...s, background: "#1e1e1e", border: "1px solid #333" }),
};

// item.price = PRECIO UNITARIO (26-ago, requerimiento Nico). Antes era el
// total de línea y al poner cantidad 4 "el precio unitario lo divide en 4".
// 27-ago (Paquete A ventas): cada línea se clasifica ref/fab/rep.
//   ref = catálogo (flujo histórico intacto: buscador + precio automático)
//   fab = fabricación a pedido: descripción libre + precio manual + planos
//   rep = reparación: ídem fab + componente reparado
// El correlativo FAB-####/REP-#### lo asigna el SERVER al guardar.
const getNewItem = () => ({
  piece: null, quantity: 1, price: "0",
  tipoTrabajo: "ref", descLibre: "", componenteReparado: "", planos: [],
});

const TIPOS_TRABAJO_CHIPS = [
  { value: "ref", label: "⚙️ REF", title: "Pieza de catálogo",                              color: "#3498db", bg: "#0f2230", border: "#1d4060" },
  { value: "fab", label: "🔨 FAB", title: "Fabricación a pedido — correlativo FAB-#### al guardar", color: "#a78bfa", bg: "#1f1635", border: "#2d2050" },
  { value: "rep", label: "🔧 REP", title: "Reparación — correlativo REP-#### al guardar",   color: "#f39c12", bg: "#2a1d08", border: "#4a3410" },
];

const esFabRep = it => it.tipoTrabajo === "fab" || it.tipoTrabajo === "rep";
// Línea "completa": ref necesita pieza del catálogo; fab/rep, descripción libre.
const itemValido = it => esFabRep(it)
  ? !!String(it.descLibre || "").trim()
  : !!it.piece;

// Total de una línea = unitario × cantidad. Único lugar donde se calcula.
const lineTotal = it =>
  Math.round((+formatInt(String(it.price)) || 0) * (Number(it.quantity) || 1));

// Condiciones que salen impresas en el PDF — editables por cotización
// (antes hardcodeadas; requerimiento Nico vía Heimdall 26-ago).
const CONDICIONES_DEFAULT = {
  plazoEntrega:    "5 a 6 días hábiles",
  lugarEntrega:    "sus bodegas",
  condicionesPago: "30 días",
  validez:         "10 días",
};

// Opciones rápidas por campo (26-ago, pedido Maxi: "no tenemos selección").
// Un toque las pone en el input; el texto libre sigue disponible para casos
// especiales. El input queda editable después de elegir.
const CONDICIONES_PRESETS = {
  plazoEntrega: ["5 a 6 días hábiles", "10 días hábiles", "15 días hábiles", "Según programa acordado"],
  condicionesPago: [
    "30 días",
    "Contado contra entrega",
    "50% con la OC · 50% contra entrega",
    "Pago anticipado",
  ],
  validez: ["10 días", "15 días", "30 días"],
};

// ─── estilos base ─────────────────────────────────────────────────────────────
const S = {
  wrap: {
    display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
    minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
    fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
  },
  card: {
    background: "#242424", border: "1px solid #2e2e2e",
    borderRadius: 8, padding: "16px 18px", marginBottom: 14,
  },
  label: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4,
  },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 5,
    padding: "7px 10px", fontSize: 12, color: "#ddd",
    fontFamily: "Arial,sans-serif", width: "100%", boxSizing: "border-box",
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: "#e30613",
    textTransform: "uppercase", letterSpacing: ".07em",
    margin: "14px 0 8px",
  },
};

// ─── componente principal ─────────────────────────────────────────────────────
const QuotationForm = () => {
  const navigate       = useNavigate();
  const navigationType = useNavigationType();
  const location       = useLocation();
  const waitRef        = useRef();
  // Rama móvil (26-ago): SOLO layout — la fila de ítems se vuelve tarjeta
  // apilada con labels propios. Lógica de precios/condiciones intacta.
  const isMobile       = useIsMobile();

  const [pieces,  setPieces]  = useState([]);
  const [clients, setClients] = useState([]);

  const [client,      setClient]      = useState(null);
  // 01-sep (pedido Maxi en el simulacro): el cliente nuevo aparece MIENTRAS se
  // arma la cotización — alta inline sin salir del form ni perder lo escrito.
  const [nuevoCliente,   setNuevoCliente]   = useState(null); // null | {name,rut,email,phone}
  const [creandoCliente, setCreandoCliente] = useState(false);
  const [draftRestaurado, setDraftRestaurado] = useState(false);
  const [draftsGuardados, setDraftsGuardados] = useState([]); // selector al abrir
  const [piezaAMedias, setPiezaAMedias] = useState(null); // borrador de Nueva Pieza visible aca
  const draftIdRef = useRef("q-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7));
  // 01-sep: destinatario. Los contactos viven en la ficha del cliente (multiples,
  // por area — caso Rocmin); aqui SOLO se elige o se agrega uno nuevo a la ficha.
  const [attnSel,        setAttnSel]        = useState(null); // {nombre,email}
  const [nuevoContacto,  setNuevoContacto]  = useState(null); // null | {nombre,email,area}
  const [creandoContacto,setCreandoContacto]= useState(false);
  const [name,        setName]        = useState("");
  const [observations,setObservations]= useState("");
  const [items,       setItems]       = useState([getNewItem()]);
  const [priceManual, setPriceManual] = useState({});
  // Descuento global de la cotización: "%" o "$" (monto). Viaja al backend
  // y al PDF; el precio NETO es el que después viaja a la OC.
  const [descTipo, setDescTipo] = useState("%");
  const [descValor, setDescValor] = useState("");
  // Condiciones impresas en el PDF — editables (26-ago).
  const [condiciones, setCondiciones] = useState({ ...CONDICIONES_DEFAULT });
  const [error,       setError]       = useState();
  const [saving,      setSaving]      = useState(false);

  const [nextNumber, setNextNumber] = useState(null);
  // Vista previa del PDF nuevo (estética Binimeliz unificada)
  const [pdfPreview, setPdfPreview] = useState(null);

  // ── Hermes (agente cotizador IA) ──
  const [hermesText,     setHermesText]     = useState("");
  const [hermesLoading,  setHermesLoading]  = useState(false);
  const [hermesError,    setHermesError]    = useState("");
  const [hermesSummary,  setHermesSummary]  = useState("");
  const [hermesWarnings, setHermesWarnings] = useState([]);
  const hermesFileRef = useRef(null);

  // Marca cuándo terminó la carga inicial (data fetched + draft restaurado).
  // Sin esto, el useEffect de auto-save dispararía con state vacío durante el
  // mount y pisaría el draft válido en localStorage.
  const loadedRef = useRef(false);

  const getData = async () => {
    try {
      const [pRes, eRes, qRes, woRes] = await Promise.all([
        backend.get("piece"),
        backend.get("entity"),
        backend.get("quotation"),
        backend.get("workOrder"),
      ]);

      // El número correlativo es maestro — incluye OTs Y cotizaciones
      // Tomamos el máximo entre: último N° de OT + último N° de cotización
      const qNums  = (qRes.data  || []).map(q => q.number || 0).filter(Boolean);
      const woNums = (woRes.data || []).map(w => w.number || 0).filter(Boolean);
      const allNums = [...qNums, ...woNums];
      setNextNumber(allNums.length ? Math.max(...allNums) + 1 : 8189);

      const pieceOptions = pRes.data.map(v => ({
        ...v, value: v._id,
        label: `${v.sku ? v.sku + " - " : ""}${v.name}`,
      }));
      setPieces(pieceOptions);
      setClients(eRes.data
        .filter(v => v.type === "client")
        .map(v => ({
          ...v, value: v._id,
          label: `${v.rut ? v.rut + " - " : ""}${v.name}`,
        })));

      // 1) Restaurar draft si existe y no está vencido. La usuaria puede haber
      // ido a /pieces/new (calculadora) y volver — sessionStorage preserva el
      // form completo (cliente, nombre, observaciones, items, priceManual).
      // Multi-borrador: NO se autocarga ninguno — se ofrecen en un selector y el
      // vendedor elige continuar uno o seguir con la nueva (la urgente escribe
      // directo y se guarda como borrador propio, sin pisar nada).
      const listaDrafts = loadDrafts();
      const resumeId = location.state?.resumeDraftId;
      const reanudar = resumeId && listaDrafts.find(d => d.draftId === resumeId);
      if (reanudar) {
        // Volvemos de la calculadora: se reanuda ESA cotizacion sin preguntar.
        cargarBorrador(reanudar);
      } else {
        setDraftsGuardados(listaDrafts);
      }
      setPiezaAMedias(loadPiezaAMedias());

      // 2) Si volvemos con una pieza recién creada en /pieces/new, agregarla.
      //    Si había una fila vacía (sin pieza seleccionada) la reemplazamos en
      //    vez de duplicar — UX más limpia.
      if (location.state?.newPiece) {
        const p = location.state.newPiece;
        const opt = { ...p, value: p._id, label: `${p.sku ? p.sku + " - " : ""}${p.name}` };
        setPieces(prev => prev.find(v => v._id === p._id) ? prev : [...prev, opt]);
        const newItem = {
          piece: opt,
          quantity: 1,
          price: p.price ? String(p.price) : "0",
        };
        setItems(prev => {
          const emptyIdx = prev.findIndex(it => !it.piece);
          if (emptyIdx >= 0) {
            const next = [...prev];
            next[emptyIdx] = newItem;
            return next;
          }
          return [...prev, newItem];
        });
        // Limpiar location.state para que un refresh no re-agregue la pieza.
        navigate(location.pathname, { replace: true, state: null });
      }

      loadedRef.current = true;
    } catch(e) { console.error(e); }
  };

  // Auto-save del draft a localStorage en cada cambio. Solo guarda si hay
  // contenido relevante para no persistir un draft vacío al primer mount.
  useEffect(() => {
    if (!loadedRef.current) return;
    const hasContent = !!client || !!name.trim() || !!observations.trim()
      || items.some(it => it.piece || String(it.descLibre || "").trim());
    if (!hasContent) {
      // Nicolas 03-sep: vaciar el formulario BORRA su propio borrador — si no,
      // quedaba zombie con el contenido viejo y reaparecia al volver de Nueva pieza.
      removeDraft(draftIdRef.current);
      return;
    }
    upsertDraft({
      draftId: draftIdRef.current,
      client, name, observations, items, priceManual, descTipo, descValor, condiciones,
      savedAt: Date.now(), attnSel,
    });
  }, [client, name, observations, items, priceManual, descTipo, descValor, condiciones]);

  const cargarBorrador = (d) => {
    draftIdRef.current = d.draftId;
    setDraftRestaurado(true);
    if (d.client)               setClient(d.client);
    if (d.name)                 setName(d.name);
    if (d.observations)         setObservations(d.observations);
    if (d.attnSel)              setAttnSel(d.attnSel);
    if (Array.isArray(d.items) && d.items.length > 0) setItems(d.items);
    if (d.priceManual)          setPriceManual(d.priceManual);
    if (d.descTipo)             setDescTipo(d.descTipo);
    if (d.descValor != null)    setDescValor(d.descValor);
    if (d.condiciones)          setCondiciones({ ...CONDICIONES_DEFAULT, ...d.condiciones });
    setDraftsGuardados([]);
  };

  const cancel = () => {
    // 02-sep: cancelar NO bota el borrador — se descarta solo explicitamente.
    return navigationType === "POP"
      ? navigate("/quotations", { replace: true })
      : navigate(-1);
  };

  // it.price = UNITARIO desde 26-ago; el subtotal suma unitario × cantidad.
  const subtotal = items.reduce((s, it) => s + lineTotal(it), 0);
  const descMonto = (() => {
    const v = +formatInt(String(descValor)) || 0;
    if (v <= 0) return 0;
    return descTipo === "%"
      ? Math.round(subtotal * Math.min(v, 100) / 100)
      : Math.min(v, subtotal);
  })();
  const total = subtotal - descMonto;

  const submit = async () => {
    setError();
    setSaving(true);
    try {
      if (!name)   throw new Error("Ingresa un nombre para la cotización");
      if (!client) throw new Error("Selecciona un cliente");
      if (!items.length) throw new Error("Agrega al menos una pieza");
      for (const it of items) {
        if (esFabRep(it)) {
          if (!String(it.descLibre || "").trim()) {
            throw new Error(`Describe el trabajo en cada línea ${it.tipoTrabajo.toUpperCase()}`);
          }
        } else if (!it.piece) {
          throw new Error("Selecciona una pieza en cada fila de catálogo (REF)");
        }
      }
      const res = await backend.post("quotation", {
        number:      nextNumber || 8189,
        description: name,
        client: client.value,
        observations,
        attn: attnSel ? { nombre: attnSel.nombre, email: attnSel.email || "" } : undefined,
        // ref → pieza de catálogo; fab/rep → descripción libre + clasificación.
        // El codigoTrabajo (FAB-####/REP-####) lo asigna el server al guardar.
        items: items.map(it => esFabRep(it)
          ? {
              name:      String(it.descLibre).trim(),
              quantity:  Number(it.quantity),
              unitPrice: Math.round(+formatInt(String(it.price)) || 0),
              tipoTrabajo: it.tipoTrabajo,
              componenteReparado: it.tipoTrabajo === "rep"
                ? String(it.componenteReparado || "").trim() || undefined
                : undefined,
              planos: it.planos || [],
            }
          : {
              piece:     it.piece.value,
              name:      it.piece.label,
              quantity:  Number(it.quantity),
              unitPrice: Math.round(+formatInt(String(it.price)) || 0),
              tipoTrabajo: "ref",
            }),
        condiciones,
        discount: {
          type:   descTipo,
          value:  +formatInt(String(descValor)) || 0,
          amount: descMonto,
        },
      });
      removeDraft(draftIdRef.current);
      navigate("/quotations", { replace: true, state: { new: res.data } });
    } catch(e) {
      setError(e?.message);
    }
    setSaving(false);
  };

  const addItem    = () => setItems(p => [...p, getNewItem()]);
  const removeItem = i  => { setItems(p => p.filter((_, idx) => idx !== i)); setError(null); };

  // ── Hermes: aplica el borrador estructurado al formulario ──
  const applyHermesDraft = draft => {
    if (!draft) return;
    if (draft.clientId) {
      const c = clients.find(x => x._id === draft.clientId);
      if (c) setClient(c);
    }
    const warnings = [];
    const newItems = [];
    const manual = {};
    (draft.items || []).forEach(it => {
      const qty = Number(it.quantity) || 1;
      if (it.pieceId) {
        const opt = pieces.find(p => p._id === it.pieceId);
        if (opt) {
          const idx = newItems.length;
          newItems.push({ piece: opt, quantity: qty, price: String(Math.round(Number(it.unitPrice) || 0)) });
          if (it.porConfirmar) {
            manual[idx] = true; // no auto-pisar el precio que el usuario ponga
            warnings.push(`⚠️ ${it.name}: ${it.nota || "por confirmar"}`);
          }
          return;
        }
      }
      // sin pieza de catálogo → no se puede agregar como fila; queda como aviso
      warnings.push(
        `➕ ${qty} × ${it.name}${it.sku ? ` (${it.sku})` : ""}: ${it.nota || "no está en el catálogo, agrégala manual"}`
      );
    });
    if (newItems.length) {
      setItems(newItems);
      setPriceManual(manual);
    }
    if (!name && draft.clientName) setName(`Cotización ${draft.clientName}`);
    setHermesWarnings(warnings);
  };

  const runHermes = async () => {
    const file = hermesFileRef.current?.files?.[0];
    if (!hermesText.trim() && !file) {
      setHermesError("Pega el pedido o sube un PDF.");
      return;
    }
    setHermesError(""); setHermesSummary(""); setHermesWarnings([]); setHermesLoading(true);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        if (hermesText.trim()) fd.append("text", hermesText.trim());
        res = await backend.post("quotation/hermes", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await backend.post("quotation/hermes", { text: hermesText.trim() });
      }
      const { summary, draft, extractWarning } = res.data;
      setHermesSummary(summary || "");
      applyHermesDraft(draft);
      if (extractWarning) setHermesWarnings(w => [extractWarning, ...w]);
    } catch (e) {
      setHermesError(e?.response?.data?.error || e.message || "Error al cotizar con Hermes");
    }
    setHermesLoading(false);
  };

  const handlePieceChange = (i, v) => {
    setItems(p => {
      const n = [...p];
      n[i] = {
        ...n[i], piece: v,
        // Auto-precio: el UNITARIO de catálogo tal cual (sin multiplicar).
        price: (!priceManual[i] && v?.price) ? String(v.price) : n[i].price,
      };
      return n;
    });
  };

  // 15-sep-2026: plano / material bruto completados desde la propia cotización.
  // Solo actualiza la pieza del ítem (y la lista de piezas), sin tocar el precio.
  const handlePieceMeta = (i, cambios) => {
    setItems(p => { const n = [...p]; if (n[i]?.piece) n[i] = { ...n[i], piece: { ...n[i].piece, ...cambios } }; return n; });
    setPieces(p => p.map(x => x._id === items[i]?.piece?._id ? { ...x, ...cambios } : x));
  };

  const handleQtyChange = (i, qty) => {
    // El unitario NO cambia con la cantidad (bug Nico 26-ago: antes el campo
    // era total y al poner 4 unidades "el precio unitario lo divide en 4").
    setItems(p => {
      const n = [...p];
      n[i] = { ...n[i], quantity: qty };
      return n;
    });
  };

  const handlePriceChange = (i, val) => {
    setPriceManual(p => ({ ...p, [i]: true }));
    setItems(p => { const n = [...p]; n[i] = { ...n[i], price: val }; return n; });
  };

  // ── Paquete A (27-ago): tipo de trabajo por línea + planos ──
  const handleTipoChange = (i, tipo) => {
    setItems(p => {
      const n = [...p];
      n[i] = { ...n[i], tipoTrabajo: tipo };
      return n;
    });
  };

  const handleItemField = (i, field, value) => {
    setItems(p => { const n = [...p]; n[i] = { ...n[i], [field]: value }; return n; });
  };

  const [planoSubiendo, setPlanoSubiendo] = useState(null); // idx de la línea subiendo
  const uploadPlano = async (i, file) => {
    if (!file) return;
    setPlanoSubiendo(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await backend.post("quotation/plano-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems(p => {
        const n = [...p];
        n[i] = { ...n[i], planos: [...(n[i].planos || []), res.data] };
        return n;
      });
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo subir el plano");
    }
    setPlanoSubiendo(null);
  };

  const removePlano = (i, idx) => {
    setItems(p => {
      const n = [...p];
      n[i] = { ...n[i], planos: (n[i].planos || []).filter((_, k) => k !== idx) };
      return n;
    });
  };

  // Chips REF/FAB/REP de una línea (mismo look que los presets de condiciones)
  const renderTipoChips = i => (
    <div style={{ marginBottom: 5 }}>
      {(items[i]?.tipoTrabajo || "ref") === "ref" && (items[i]?.descLibre || "").trim() !== "" && (
        <div style={{ fontSize: 10, color: "#f39c12", marginBottom: 3 }}>
          ✎ Su descripción quedó guardada: “{String(items[i].descLibre).slice(0, 60)}” — vuelve al cambiar a FAB o REP (nada se borra).
        </div>
      )}
    <div style={{ display: "flex", gap: 5, marginBottom: 5 }}>
      {TIPOS_TRABAJO_CHIPS.map(t => {
        const activo = (items[i]?.tipoTrabajo || "ref") === t.value;
        return (
          <button
            key={t.value}
            type="button"
            title={t.title}
            onClick={() => handleTipoChange(i, t.value)}
            style={{
              background: activo ? t.bg : "#1e1e1e",
              color: activo ? t.color : "#777",
              border: `1px solid ${activo ? t.border : "#2e2e2e"}`,
              borderRadius: 5, padding: "3px 9px", fontSize: 10,
              fontWeight: activo ? 700 : 400,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
    </div>
  );

  // Bloque extra de una línea FAB/REP: componente reparado (rep) + planos
  const renderFabRepExtras = (item, i) => (
    <div style={{ marginTop: 8 }}>
      {item.tipoTrabajo === "rep" && (
        <div style={{ marginBottom: 8 }}>
          <div style={S.label}>Componente reparado (texto libre o SKU)</div>
          <input
            value={item.componenteReparado || ""}
            onChange={e => handleItemField(i, "componenteReparado", e.target.value)}
            placeholder='Ej: "Vástago cilindro levante" o SKU 3222 3280 58'
            style={S.input}
          />
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <label style={{
          background: "#1e1e1e", color: "#cbd5e1", border: "1px dashed #3a3a3a",
          borderRadius: 5, padding: "4px 10px", fontSize: 11, cursor: "pointer",
        }}>
          {planoSubiendo === i ? "Subiendo…" : "📎 Subir plano"}
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            disabled={planoSubiendo === i}
            onChange={e => { uploadPlano(i, e.target.files?.[0]); e.target.value = ""; }}
          />
        </label>
        {(item.planos || []).map((p, idx) => (
          <span key={p.key || idx} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "#161616", border: "1px solid #333", borderRadius: 5,
            padding: "3px 8px", fontSize: 10, color: "#cbd5e1", maxWidth: 220,
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              📄 {p.nombre}
            </span>
            <span
              onClick={() => removePlano(i, idx)}
              title="Quitar plano de esta línea"
              style={{ color: "#999", cursor: "pointer", fontWeight: 700 }}
            >×</span>
          </span>
        ))}
        <span style={{ fontSize: 10, color: "#777" }}>
          El correlativo {item.tipoTrabajo === "rep" ? "REP-####" : "FAB-####"} se asigna al guardar.
        </span>
      </div>
    </div>
  );

  // Datos cocinados para el nuevo BinimelizPDFLayout (estética unificada).
  // Se renderiza como overlay React en lugar del popup HTML viejo.
  const buildPdfData = () => {
    const cotNum = String(nextNumber || "—");
    const itemsForPdf = items.filter(itemValido).map(it => ({
      cant:           it.quantity || 1,
      detalle: esFabRep(it)
        ? [
            `[${it.tipoTrabajo.toUpperCase()}] `,
            it.descLibre,
            it.tipoTrabajo === "rep" && String(it.componenteReparado || "").trim()
              ? ` — componente: ${String(it.componenteReparado).trim()}`
              : "",
          ].join("")
        : [
            it.piece.sku ? `[${it.piece.sku}] ` : "",
            it.piece.name || it.piece.label,
          ].join(""),
      valorUnitario:  Math.round(+formatInt(String(it.price)) || 0),
      valorTotal:     lineTotal(it),
    }));
    return {
      tipo:    "cotizacion",
      numero:  cotNum,
      fecha:   new Date(),
      recipiente: {
        rolLabel:        "INFORMACION CLIENTE.",
        razon:           client?.label || client?.name || "—",
        rut:             client?.rut || "—",
        direccion:       client?.address || "—",
        contactoNombre:  client?.contactName || "—",
        contactoEmail:   client?.email || "—",
        contactoTel:     client?.phone || "—",
      },
      items: itemsForPdf,
      descuentoPct:   descTipo === "%" ? (+formatInt(String(descValor)) || 0) : 0,
      descuentoMonto: descTipo === "$" && descMonto > 0 ? descMonto : null,
      nota:  name || "",
      observaciones: { ...CONDICIONES_DEFAULT, ...condiciones },
      footer: {
        firmanteNombre: "Nicolás Herrera",
        firmanteCargo:  "p.p. BINIMELIZ S. A.",
      },
    };
  };

  const printQuotation = () => {
    if (!client) { setError("Selecciona un cliente antes de generar el PDF"); return; }
    if (!name)   { setError("Ingresa un nombre antes de generar el PDF"); return; }
    setError();
    setPdfPreview(buildPdfData());
  };
// ─── PDF popup legacy eliminado en favor de BinimelizPDFLayout ───

  return (
    <WaitAsync func={getData} ref={waitRef}>
      <div style={S.wrap}>

        {/* ── Header ── (móvil: los botones bajan de línea si no caben) */}
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: isMobile ? "wrap" : undefined, gap: isMobile ? 8 : undefined }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Nueva Cotización</div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
              {nextNumber ? `Próximo N°: ${nextNumber}` : "Binimeliz S.A."}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"row", gap:8 }}>
            <button onClick={printQuotation} style={{
              background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020",
              borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700,
            }}>↓ Generar PDF</button>
            <button onClick={cancel} style={{
              background: "transparent", color: "#cbd5e1", border: "1px solid #444",
              borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer",
            }}>← Volver</button>
          </div>
        </div>

        {/* ── Panel Hermes (cotizador IA) ── */}
        <div style={{ ...S.card, border: "1px solid #3a2f0e", background: "#211d12" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15 }}>🪽</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#e8c860", letterSpacing: ".04em" }}>
              COTIZAR CON HERMES
            </span>
            <span style={{ fontSize: 10, color: "#8a7a45" }}>
              pega el pedido del cliente o sube el plano/OC — Hermes lo arma
            </span>
          </div>
          <textarea
            value={hermesText}
            onChange={e => setHermesText(e.target.value)}
            rows={3}
            placeholder="Ej: De parte de Rocmin: 4 pasadores 3222 3102 25, 2 bushing 4350 2650 47, 10 wear tube 3222 3409 47…"
            style={{ ...S.input, resize: "vertical", minHeight: 54, fontFamily: "inherit" }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
            <input
              ref={hermesFileRef}
              type="file"
              accept="application/pdf"
              style={{ fontSize: 11, color: "#aaa", flex: 1, minWidth: 160 }}
            />
            <button
              onClick={runHermes}
              disabled={hermesLoading}
              style={{
                background: "#3a2f0e", color: "#e8c860", border: "1px solid #5a4a18",
                borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 700,
                cursor: "pointer", opacity: hermesLoading ? 0.7 : 1, whiteSpace: "nowrap",
              }}
            >
              {hermesLoading ? "Hermes pensando…" : "🪽 Cotizar"}
            </button>
          </div>
          {hermesError && (
            <div style={{ color: "#e74c3c", fontSize: 11, marginTop: 8 }}>{hermesError}</div>
          )}
          {hermesSummary && (
            <div style={{
              marginTop: 10, padding: "8px 10px", background: "#161205",
              borderRadius: 6, fontSize: 12, color: "#d8c68a", whiteSpace: "pre-wrap",
            }}>
              {hermesSummary}
            </div>
          )}
          {hermesWarnings.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#d39a3a" }}>
              {hermesWarnings.map((w, i) => (
                <div key={i} style={{ padding: "2px 0" }}>{w}</div>
              ))}
            </div>
          )}
        </div>

        <div style={S.card}>

          {/* nombre + cliente (móvil: apilados) */}
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, marginBottom: 12 }}>
            <div style={{ flex: isMobile ? undefined : 2 }}>
              <div style={S.label}>Nombre / descripción</div>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ej: Mordazas 480 x40 + Spacer x8"
                style={S.input}
              />
            </div>
            <div style={{ flex: 1 }}>
              {draftsGuardados.length > 0 && (
                <div style={{ background: "#241a12", border: "1px solid #4a3410", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f39c12", marginBottom: 6 }}>
                    📂 Tiene {draftsGuardados.length} cotización{draftsGuardados.length === 1 ? "" : "es"} a medias (se guardan solas, duran 24 h). Puede continuar una o simplemente escribir la nueva — nada se pisa.
                  </div>
                  {draftsGuardados.map(d => (
                    <div key={d.draftId} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                      <button type="button" onClick={() => cargarBorrador(d)}
                        style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020", borderRadius: 5, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                        ▶ Continuar
                      </button>
                      <span style={{ fontSize: 11, color: "#cbd5e1", flex: 1 }}>{etiquetaDraft(d)}</span>
                      <button type="button" title="Eliminar este borrador"
                        onClick={() => { removeDraft(d.draftId); setDraftsGuardados(loadDrafts()); }}
                        style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 12 }}>✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setDraftsGuardados([])}
                    style={{ background: "transparent", color: "#8a8a8a", border: "1px solid #444", borderRadius: 5, padding: "3px 10px", fontSize: 10, cursor: "pointer", marginTop: 2 }}>
                    Seguir con esta nueva (ocultar lista)
                  </button>
                </div>
              )}
              <div style={S.label}>Cliente</div>
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <Select
                    styles={selectStyles}
                    isClearable
                    onChange={c => {
                      setClient(c);
                      const cs = contactosDe(c);
                      setAttnSel(cs.length ? cs[0] : null);
                    }}
                    value={client}
                    options={clients}
                    placeholder="Seleccionar"
                    classNamePrefix="react-select"
                  />
                </div>
                <button type="button" title="Registrar un cliente nuevo sin salir de la cotización"
                  onClick={() => setNuevoCliente(v => v ? null : { name: "", rut: "", email: "", phone: "", contacto: "", direccion: "" })}
                  style={{ background: "#0d1a2d", color: "#3498db", border: "1px solid #1d3a5f", borderRadius: 6, padding: "0 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  ＋ Nuevo
                </button>
              </div>
              {nuevoCliente && (
                <div style={{ marginTop: 8, padding: 10, background: "#1a2230", border: "1px solid #1d3a5f", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#3498db", marginBottom: 6 }}>Cliente nuevo (queda en Entidades al guardar)</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <input placeholder="Razón social *" value={nuevoCliente.name} onChange={e => setNuevoCliente(v => ({ ...v, name: e.target.value }))} style={{ ...S.input, flex: 2, minWidth: 160 }} />
                    <input placeholder="RUT" value={nuevoCliente.rut} onChange={e => setNuevoCliente(v => ({ ...v, rut: maskRut(e.target.value) }))} style={{ ...S.input, flex: 1, minWidth: 110 }} />
                    <input placeholder="Email" value={nuevoCliente.email} onChange={e => setNuevoCliente(v => ({ ...v, email: e.target.value }))} style={{ ...S.input, flex: 2, minWidth: 160 }} />
                    <input placeholder="Teléfono" value={nuevoCliente.phone} onChange={e => setNuevoCliente(v => ({ ...v, phone: maskPhone(e.target.value) }))} style={{ ...S.input, flex: 1, minWidth: 110 }} />
                    <input placeholder="Contacto (solicitante)" value={nuevoCliente.contacto} onChange={e => setNuevoCliente(v => ({ ...v, contacto: e.target.value }))} style={{ ...S.input, flex: 2, minWidth: 160 }} />
                    <input placeholder="Dirección del recinto" value={nuevoCliente.direccion} onChange={e => setNuevoCliente(v => ({ ...v, direccion: e.target.value }))} style={{ ...S.input, flex: 2, minWidth: 160 }} />
                  </div>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 8 }}>
                    <button type="button" onClick={() => setNuevoCliente(null)} style={{ background: "transparent", color: "#cbd5e1", border: "1px solid #444", borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer" }}>Cancelar</button>
                    <button type="button" disabled={creandoCliente || !(nuevoCliente.name || "").trim()}
                      onClick={async () => {
                        setCreandoCliente(true);
                        try {
                          const res = await backend.post("entity", { name: nuevoCliente.name.trim(), rut: nuevoCliente.rut.trim() || undefined, email: nuevoCliente.email.trim() || undefined, phone: nuevoCliente.phone.trim() || undefined, contactPerson: (nuevoCliente.contacto || "").trim() || undefined, address: (nuevoCliente.direccion || "").trim() || undefined, type: "client" });
                          const e2 = res.data || {};
                          const opt = { ...e2, value: e2._id, label: (e2.rut ? e2.rut + " - " : "") + e2.name };
                          setClients(prev => [opt, ...prev]);
                          setClient(opt);
                          const cs0 = contactosDe(opt);
                          setAttnSel(cs0.length ? cs0[0] : null);
                          setNuevoCliente(null);
                        } catch (err) {
                          alert("No se pudo crear el cliente: " + (err?.response?.data?.error || err?.response?.data || err.message));
                        }
                        setCreandoCliente(false);
                      }}
                      style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020", borderRadius: 6, padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", opacity: creandoCliente ? 0.7 : 1 }}>
                      {creandoCliente ? "Guardando…" : "Guardar cliente"}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <div style={{ ...S.label, fontSize: 10 }}>Atención a (contacto de la ficha del cliente)</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <select value={attnSel ? attnSel.nombre : ""} disabled={!client}
                    onChange={e => { const cs = contactosDe(client); setAttnSel(cs.find(x => x.nombre === e.target.value) || null); }}
                    style={{ ...S.input, flex: 2, minWidth: 180 }}>
                    {!client && <option value="">Elija primero el cliente</option>}
                    {client && contactosDe(client).length === 0 && <option value="">La ficha no tiene contactos — agregue uno</option>}
                    {client && contactosDe(client).map(x => (
                      <option key={x.nombre} value={x.nombre}>{x.nombre}{x.area ? " (" + x.area + ")" : ""}{x.email ? " · " + x.email : ""}</option>
                    ))}
                  </select>
                  <button type="button" disabled={!client} title="Agregar un contacto nuevo a la ficha del cliente (queda guardado para siempre)"
                    onClick={() => setNuevoContacto(v => v ? null : { nombre: "", email: "", area: "" })}
                    style={{ background: "#0d1a2d", color: "#3498db", border: "1px solid #1d3a5f", borderRadius: 6, padding: "0 12px", fontSize: 12, fontWeight: 700, cursor: client ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                    ＋ Contacto
                  </button>
                </div>
                {nuevoContacto && (
                  <div style={{ marginTop: 6, padding: 8, background: "#1a2230", border: "1px solid #1d3a5f", borderRadius: 8, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <input placeholder="Nombre *" value={nuevoContacto.nombre} onChange={e => setNuevoContacto(v => ({ ...v, nombre: e.target.value }))} style={{ ...S.input, flex: 2, minWidth: 140 }} />
                    <input placeholder="Email" value={nuevoContacto.email} onChange={e => setNuevoContacto(v => ({ ...v, email: e.target.value }))} style={{ ...S.input, flex: 2, minWidth: 140 }} />
                    <input placeholder="Área (compras, bodega…)" value={nuevoContacto.area} onChange={e => setNuevoContacto(v => ({ ...v, area: e.target.value }))} style={{ ...S.input, flex: 1, minWidth: 120 }} />
                    <button type="button" disabled={creandoContacto || !(nuevoContacto.nombre || "").trim()}
                      onClick={async () => {
                        setCreandoContacto(true);
                        try {
                          const res = await backend.post("entity/" + client.value + "/contacto", { nombre: nuevoContacto.nombre.trim(), email: nuevoContacto.email.trim(), area: nuevoContacto.area.trim() });
                          const e2 = res.data;
                          const opt = { ...client, ...e2, value: e2._id, label: client.label };
                          setClients(prev => prev.map(x => x.value === opt.value ? opt : x));
                          setClient(opt);
                          setAttnSel({ nombre: nuevoContacto.nombre.trim(), email: nuevoContacto.email.trim(), area: nuevoContacto.area.trim() });
                          setNuevoContacto(null);
                        } catch (err) {
                          alert("No se pudo agregar el contacto: " + (err && err.response && err.response.data && err.response.data.error ? err.response.data.error : err.message));
                        }
                        setCreandoContacto(false);
                      }}
                      style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020", borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", opacity: creandoContacto ? 0.7 : 1 }}>
                      {creandoContacto ? "Guardando…" : "Guardar en la ficha"}
                    </button>
                  </div>
                )}
                <div style={{ marginTop: 4, fontSize: 10, color: "#7f8c8d" }}>🖊 La cotización sale firmada con su usuario (vendedor emisor) al guardar.</div>
              </div>
              {draftRestaurado && (
                <div style={{ marginTop: 6, fontSize: 11, color: "#f39c12" }}>
                  💾 Se restauró un borrador guardado. {" "}
                  <button type="button" onClick={() => { removeDraft(draftIdRef.current); window.location.reload(); }}
                    style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 11, textDecoration: "underline", padding: 0 }}>
                    Descartar y partir de cero
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* observaciones */}
          <div style={{ marginBottom: 12 }}>
            <div style={S.label}>Observaciones / nota interna</div>
            <textarea
              value={observations}
              onChange={e => setObservations(e.target.value)}
              placeholder="Quién solicitó, datos del contacto, condiciones especiales, plazos, etc."
              rows={2}
              style={{ ...S.input, resize: "vertical", minHeight: 50, fontFamily: "inherit" }}
            />
          </div>

          {/* condiciones del PDF — editables (26-ago, requerimiento Nico) */}
          <div style={S.sectionTitle}>Condiciones (salen en el PDF)</div>
          <div style={{ display: "flex", flexDirection: "row", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            {[
              ["plazoEntrega",    "Plazo de entrega"],
              ["lugarEntrega",    "Lugar de entrega"],
              ["condicionesPago", "Condiciones de pago"],
              ["validez",         "Validez de la oferta"],
            ].map(([k, label]) => (
              <div key={k} style={{ flex: 1, minWidth: 170 }}>
                <div style={S.label}>{label}</div>
                <input
                  value={condiciones[k] ?? ""}
                  onChange={e => setCondiciones(c => ({ ...c, [k]: e.target.value }))}
                  placeholder={CONDICIONES_DEFAULT[k]}
                  style={S.input}
                />
                {CONDICIONES_PRESETS[k] && (
                  <div className="chips-scroll" style={{ display: "flex", gap: 5, marginTop: 5, flexWrap: "wrap" }}>
                    {CONDICIONES_PRESETS[k].map(opt => {
                      const activo = (condiciones[k] ?? "") === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          className="chip-touch no-touch-min"
                          onClick={() => setCondiciones(c => ({ ...c, [k]: opt }))}
                          style={{
                            background: activo ? "#3a1114" : "#1e1e1e",
                            color: activo ? "#ff8a8f" : "#999",
                            border: `1px solid ${activo ? "#e30613" : "#333"}`,
                            borderRadius: 5, padding: "3px 8px", fontSize: 10,
                            cursor: "pointer", whiteSpace: "nowrap",
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* items */}
          <div style={S.sectionTitle}>Productos</div>

          {/* Encabezados — dejan explícito que el precio es UNITARIO (26-ago).
              En móvil no van: cada ítem-tarjeta lleva sus propios labels. */}
          {!isMobile && (
          <div style={{ display: "flex", flexDirection: "row", gap: 8, marginBottom: 4 }}>
            <span style={{ ...S.label, width: 65, textAlign: "center", marginBottom: 0 }}>Cant</span>
            <span style={{ ...S.label, flex: 1, marginBottom: 0 }}>Pieza</span>
            <span style={{ ...S.label, width: 140, textAlign: "right", marginBottom: 0 }}>Precio unitario</span>
            <span style={{ ...S.label, minWidth: 80, textAlign: "right", marginBottom: 0 }}>Total línea</span>
            <span style={{ width: 30, flexShrink: 0 }} />
          </div>
          )}

          {items.map((item, i) => isMobile ? (
            /* ── Ítem móvil: tarjeta apilada. Mismos handlers, solo layout. ── */
            <div key={i} style={{ background: "#1f1f1f", border: "1px solid #2e2e2e", borderRadius: 10, padding: "10px 10px 12px", marginBottom: 8 }}>
              {renderTipoChips(i)}
              {esFabRep(item) ? (
                <>
                  <div style={S.label}>{item.tipoTrabajo === "rep" ? "Descripción de la reparación" : "Descripción del trabajo a fabricar"}</div>
                  <input
                    value={item.descLibre || ""}
                    onChange={e => handleItemField(i, "descLibre", e.target.value)}
                    placeholder={item.tipoTrabajo === "rep" ? "Ej: Reparación vástago cilindro levante" : "Ej: Fabricación eje según plano adjunto"}
                    style={S.input}
                  />
                </>
              ) : (
                <>
                  <div style={S.label}>Pieza</div>
                  <Select
                    styles={selectStyles}
                    isClearable
                    onChange={v => handlePieceChange(i, v)}
                    value={item.piece}
                    options={pieces}
                    placeholder="Seleccionar pieza"
                    classNamePrefix="react-select"
                  />
                </>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <div style={{ width: 84, flexShrink: 0 }}>
                  <div style={S.label}>Cant</div>
                  <input
                    type="number" min={1} value={item.quantity}
                    onFocus={e => e.target.select()}
                    onChange={e => handleQtyChange(i, Number(e.target.value) || 1)}
                    style={{ ...S.input, textAlign: "center" }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.label}>Precio unitario</div>
                  <div style={{ position: "relative" }}>
                    <input
                      value={`$${formatPrice(+formatInt(String(item.price)))}`}
                      onFocus={e => e.target.select()}
                      onChange={e => handlePriceChange(i, formatInt(e.target.value.replace(/\D/g,"")))}
                      style={{ ...S.input, textAlign: "right", paddingRight: 28 }}
                    />
                    <span
                      title={priceManual[i] ? "Precio manual" : "Precio automático"}
                      onClick={() => setPriceManual(p => ({ ...p, [i]: !p[i] }))}
                      style={{
                        position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                        fontSize: 10, color: priceManual[i] ? "#f39c12" : "#444",
                        fontWeight: 700, cursor: "pointer", userSelect: "none",
                      }}
                    >{priceManual[i] ? "M" : "A"}</span>
                  </div>
                </div>
              </div>
              {esFabRep(item) && renderFabRepExtras(item, i)}
              {!esFabRep(item) && item.piece?._id && (
                <PiezaPlanoMaterial pieza={item.piece} compacto onChange={c => handlePieceMeta(i, c)} />
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                <button onClick={() => removeItem(i)} className="t-02" style={{
                  border: "1px solid #2e2e2e", background: "transparent",
                  color: "#999", borderRadius: 10, padding: "6px 14px", fontSize: 12, cursor: "pointer",
                }}>× Quitar</button>
                <span style={{ fontSize: 13, fontWeight: 700, color: itemValido(item) ? "#2ecc71" : "#555", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  <span style={{ ...S.label, display: "inline", marginRight: 6 }}>Total línea</span>
                  ${formatPrice(lineTotal(item))}
                </span>
              </div>
            </div>
          ) : (
            <div key={i} style={{
              marginBottom: 8,
              ...(esFabRep(item)
                ? { background: "#1f1f1f", border: "1px solid #2e2e2e", borderRadius: 8, padding: "8px 8px 10px" }
                : {}),
            }}>
              {renderTipoChips(i)}
              <div style={{ display: "flex", flexDirection: "row", gap: 8, alignItems: "center" }}>
                <input
                  type="number" min={1} value={item.quantity}
                  onFocus={e => e.target.select()}
                  onChange={e => handleQtyChange(i, Number(e.target.value) || 1)}
                  style={{ ...S.input, width: 65, textAlign: "center" }}
                />
                <div style={{ flex: 1 }}>
                  {esFabRep(item) ? (
                    <input
                      value={item.descLibre || ""}
                      onChange={e => handleItemField(i, "descLibre", e.target.value)}
                      placeholder={item.tipoTrabajo === "rep"
                        ? "Descripción de la reparación (ej: Reparación vástago cilindro levante)"
                        : "Descripción del trabajo a fabricar (ej: Eje según plano adjunto)"}
                      style={S.input}
                    />
                  ) : (
                    <Select
                      styles={selectStyles}
                      isClearable
                      onChange={v => handlePieceChange(i, v)}
                      value={item.piece}
                      options={pieces}
                      placeholder="Seleccionar pieza"
                      classNamePrefix="react-select"
                    />
                  )}
                </div>
                <div style={{ position: "relative", width: 140 }}>
                  <input
                    value={`$${formatPrice(+formatInt(String(item.price)))}`}
                    onFocus={e => e.target.select()}
                    onChange={e => handlePriceChange(i, formatInt(e.target.value.replace(/\D/g,"")))}
                    style={{ ...S.input, textAlign: "right", paddingRight: 28 }}
                  />
                  <span
                    title={priceManual[i] ? "Precio manual" : "Precio automático"}
                    onClick={() => setPriceManual(p => ({ ...p, [i]: !p[i] }))}
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      fontSize: 10, color: priceManual[i] ? "#f39c12" : "#444",
                      fontWeight: 700, cursor: "pointer", userSelect: "none",
                    }}
                  >{priceManual[i] ? "M" : "A"}</span>
                </div>
                <span style={{ fontSize: 11, color: itemValido(item) ? "#2ecc71" : "#555", minWidth: 80, textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                  ${formatPrice(lineTotal(item))}
                </span>
                <button onClick={() => removeItem(i)} style={{
                  width: 30, height: 30, border: "none", background: "#333",
                  color: "#cbd5e1", borderRadius: 5, cursor: "pointer", flexShrink: 0,
                }}>×</button>
              </div>
              {esFabRep(item) && renderFabRepExtras(item, i)}
              {!esFabRep(item) && item.piece?._id && (
                <PiezaPlanoMaterial pieza={item.piece} compacto onChange={c => handlePieceMeta(i, c)} />
              )}
            </div>
          ))}

          {/* botones agregar */}
          <div style={{ display: "flex", flexDirection: "row", gap: 8, marginBottom: 14 }}>
            <button onClick={addItem} style={{
              flex: 1, background: "#2a2a2a", color: "#cbd5e1",
              border: "1px dashed #3a3a3a", borderRadius: 5,
              padding: "7px", fontSize: 12, cursor: "pointer",
            }}>
              <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />
              Agregar del catálogo
            </button>
            <button
              onClick={() => navigate("/pieces/new", { state: { fromQuotation: true, quotationDraftId: draftIdRef.current } })}
              style={{
                flex: 1, background: "#1e1535", color: "#a78bfa",
                border: "1px solid #3a2570", borderRadius: 5,
                padding: "7px", fontSize: 12, cursor: "pointer", fontWeight: 700,
              }}
            >
              <i className="bi bi-calculator" style={{ marginRight: 6 }} />
              Nueva pieza (calculadora)
            </button>
          </div>
          {piezaAMedias && (
            <div style={{ background: "#151a2e", border: "1px solid #2a3a6a", borderRadius: 6, padding: "7px 10px", marginTop: -8, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: "#a7b8f0", flex: 1 }}>
                🧩 Tiene una pieza a medias en la calculadora: <b>{piezaAMedias.name || piezaAMedias.sku || "sin nombre"}</b> — su avance esta guardado.
              </span>
              <button type="button"
                onClick={() => navigate("/pieces/new", { state: { fromQuotation: true, quotationDraftId: draftIdRef.current } })}
                style={{ background: "#1e1535", color: "#a78bfa", border: "1px solid #3a2570", borderRadius: 5, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                ▶ Continuar pieza
              </button>
            </div>
          )}

          {/* resumen */}
          {items.some(itemValido) && (
            <>
              <div style={S.sectionTitle}>Resumen</div>
              {items.filter(itemValido).map((it, i) => (
                <div key={i} style={{
                  display: "flex", flexDirection: "row", justifyContent: "space-between",
                  padding: "5px 0", borderBottom: "1px solid #252525", fontSize: 12,
                }}>
                  <span style={{ color: "#bbb" }}>
                    {it.quantity} × {esFabRep(it) ? `[${it.tipoTrabajo.toUpperCase()}] ${it.descLibre}` : it.piece.label}
                    <span style={{ color: "#777" }}> · ${formatPrice(+formatInt(String(it.price)) || 0)} c/u</span>
                  </span>
                  <span style={{ color: "#2ecc71", fontWeight: 600 }}>
                    ${formatPrice(lineTotal(it))}
                  </span>
                </div>
              ))}
              {/* descuento global: % o monto — el neto es lo que viaja a la OC */}
              <div style={{
                display: "flex", flexDirection: "row", justifyContent: "flex-end",
                alignItems: "center", gap: 8, marginTop: 10, fontSize: 12,
                flexWrap: isMobile ? "wrap" : undefined,
              }}>
                <span style={{ color: "#bbb" }}>Subtotal ${formatPrice(subtotal)}</span>
                <span style={{ color: "#bbb", marginLeft: 12 }}>Descuento</span>
                <select
                  value={descTipo}
                  onChange={e => setDescTipo(e.target.value)}
                  style={{
                    background: "#151515", color: "#eee", border: "1px solid #333",
                    borderRadius: 6, padding: "4px 6px", fontSize: 12,
                  }}
                >
                  <option value="%">%</option>
                  <option value="$">$</option>
                </select>
                <input
                  value={descValor}
                  onChange={e => setDescValor(e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder="0"
                  style={{
                    width: 80, background: "#151515", color: "#eee", border: "1px solid #333",
                    borderRadius: 6, padding: "4px 8px", fontSize: 12, textAlign: "right",
                  }}
                />
                {descMonto > 0 && (
                  <span style={{ color: "#f39c12", fontWeight: 600 }}>−${formatPrice(descMonto)}</span>
                )}
              </div>
              <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 10 }}>
                Total <span style={{ color: "#2ecc71" }}>${formatPrice(total)}</span>
              </div>
            </>
          )}

          {error && (
            <div style={{ color: "#e74c3c", fontSize: 12, textAlign: "center", margin: "10px 0" }}>{error}</div>
          )}

          {/* acciones (móvil: bajan de línea si no caben) */}
          <div style={{ display: "flex", flexDirection: "row", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: isMobile ? "wrap" : undefined }}>
            <button onClick={cancel} style={{
              background: "transparent", color: "#cbd5e1", border: "1px solid #444",
              borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer",
            }}>Cancelar</button>
            <button type="button" title="El borrador se guarda solo (dura 1 hora): puedes salir y retomar donde quedaste"
              onClick={() => navigate("/quotations")}
              style={{ background: "#2a230d", color: "#f39c12", border: "1px solid #4a3a10", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>
              💾 Salir (borrador queda guardado)
            </button>
            <button onClick={printQuotation} style={{
              background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020",
              borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700,
            }}>↓ Generar PDF</button>
            <button onClick={submit} disabled={saving} style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: 6, padding: "7px 18px", fontSize: 12,
              fontWeight: 700, cursor: "pointer", opacity: saving ? 0.7 : 1,
            }}>{saving ? "Creando…" : "Crear cotización"}</button>
          </div>

        </div>
      </div>
      {/* PDF preview con layout Binimeliz unificado */}
      {pdfPreview && (
        <BinimelizPDFLayout
          {...pdfPreview}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </WaitAsync>
  );
};

export default QuotationForm;
